import React, { useState, useEffect, useRef } from "react";
import api from "../../api/axios";
import { toast, ToastContainer } from "react-toastify";
import { addDays, format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

// June 2026 CASUAL working schedule. `hour` is the value persisted by the API.
const overtimeSchedule = [
  { hour: "16:00 - 17:00", label: "16:00 - 17:00", duration: 1, overtime: true },
  { hour: "17:00 - 18:00", label: "17:00 - 18:00", duration: 1, overtime: true },
  { hour: "18:00 - 19:00", label: "18:00 - 19:00", duration: 1, overtime: true },
  { hour: "19:00 - 20:00", label: "19:00 - 20:00", duration: 1, overtime: true },
];

const regularSchedule = [
  { hour: "06:30 - 07:30", label: "06:30 - 07:30", duration: 1 },
  { hour: "07:30 - 08:30", label: "07:30 - 08:30", duration: 1 },
  { hour: "08:30 - 09:30", label: "08:30 - 09:30", duration: 1 },
  { hour: "09:30 - 10:30", label: "09:30 - 10:30", duration: 1 },
  { hour: "10:30 - 11:30", label: "10:30 - 11:30", duration: 1 },
  { hour: "11:30 - 12:00", label: "11:30 - 12:00", duration: 0.5 },
  { hour: "12:00 - 12:30", label: "12:00 - 12:30", pause: true },
  { hour: "12:30 - 13:00", label: "12:30 - 13:00", duration: 0.5 },
  { hour: "13:00 - 14:00", label: "13:00 - 14:00", duration: 1 },
  { hour: "14:00 - 15:00", label: "14:00 - 15:00", duration: 1 },
  { hour: "15:00 - 16:00", label: "15:00 - 16:00", duration: 1 },
  ...overtimeSchedule,
];

const fridaySchedule = [
  { hour: "06:30 - 07:30", label: "06:30 - 07:30", duration: 1 },
  { hour: "07:30 - 08:30", label: "07:30 - 08:30", duration: 1 },
  { hour: "08:30 - 09:30", label: "08:30 - 09:30", duration: 1 },
  { hour: "09:30 - 10:00", label: "09:30 - 10:00", duration: 0.5 },
  { hour: "10:00 - 10:15", label: "10:00 - 10:15", pause: true },
  { hour: "10:15 - 10:45", label: "10:15 - 10:45", duration: 0.5 },
  { hour: "10:45 - 11:45", label: "10:45 - 11:45", duration: 1 },
  { hour: "11:45 - 12:45", label: "11:45 - 12:45", duration: 1 },
  { hour: "12:45 - 13:00", label: "12:45 - 13:00", duration: 0.25 },
  { hour: "13:00 - 15:00", label: "13:00 - 15:00", pause: true },
  { hour: "15:00 - 15:45", label: "15:00 - 15:45", duration: 0.75 },
  { hour: "15:45 - 16:00", label: "15:45 - 16:00", duration: 0.25 },
  ...overtimeSchedule,
];

const getScheduleForDate = (date) =>
  date.getDay() === 5 ? fridaySchedule : regularSchedule;

const getWorkSlotsForDate = (date) =>
  getScheduleForDate(date).filter((slot) => !slot.pause);

const getPlannedHoursForDate = (date) =>
  getScheduleForDate(date)
    .filter((slot) => !slot.pause && !slot.overtime)
    .reduce((total, slot) => total + slot.duration, 0);

function FullCalendar({ url, planningData }) {
  const [events, setEvents] = useState({});
  const [editingEvent, setEditingEvent] = useState(null);
  const [datesRange, setDatesRange] = useState([]);
  const [totalModelsFinished, setTotalModelsFinished] = useState(0);
  const inputRef = useRef(null); // Ref for input element

  useEffect(() => {
    if (planningData.start_date && planningData.end_date) {
      generateDatesRange(
        parseISO(planningData.start_date),
        parseISO(planningData.end_date)
      );
    }
    fetchEvents();
  }, [planningData.start_date, planningData.end_date]);

  const generateDatesRange = (startDate, endDate) => {
    const range = [];
    let currentDate = startDate;
    while (currentDate <= endDate) {
      range.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    setDatesRange(range);
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get(`${url}/${planningData.id}`);
      const eventsData = Array.isArray(response.data)
        ? response.data
        : [response.data];
      const fetchedEvents = eventsData.reduce((acc, event) => {
        const isInsidePlan =
          event.date >= planningData.start_date &&
          event.date <= planningData.end_date;

        if (!isInsidePlan) {
          return acc;
        }

        acc[`${event.date}-${event.hour}`] = event.models_finished;
        return acc;
      }, {});
      setEvents(fetchedEvents);

      // Sum the same unique cells that are rendered. The API should not return
      // duplicates, but using the keyed map keeps the displayed cumulative
      // quantity correct if legacy duplicate rows still exist.
      const total = Object.values(fetchedEvents).reduce(
        (sum, value) => sum + (Number(value) || 0),
        0
      );
      setTotalModelsFinished(total);
    } catch (error) {
      toast.error("Erreur : " + error);
    }
  };

  const handleDoubleClick = (date, hour) => {
    setEditingEvent({ date, hour });
  };

  const handleEventChange = (e) => {
    const { date, hour } = editingEvent;
    const newEvents = { ...events, [`${date}-${hour}`]: e.target.value };
    setEvents(newEvents);
  };

  const handleEventBlur = async () => {
    const { date, hour } = editingEvent;
    const eventValue = parseInt(events[`${date}-${hour}`], 10) || 0;

    try {
      const existingEventResponse = await api.post(`${url}/search`, {
        product_plan_id: planningData.id,
        date: date,
        hour: hour,
      });

      if (existingEventResponse.data.length > 0) {
        if (eventValue) {
          await api.post(`${url}/${existingEventResponse.data[0].id}`, {
            product_plan_id: planningData.id,
            date: date,
            hour: hour,
            models_finished: eventValue,
          });
          const newTotal =
            totalModelsFinished -
            existingEventResponse.data[0].models_finished +
            eventValue;
          setTotalModelsFinished(newTotal);

          if (newTotal > planningData.qte) {
            toast.error(
              "La quantité ne peut pas être supérieure à Quantité Société de modèle."
            );
            setEvents({ ...events, [`${date}-${hour}`]: 0 }); // Reset to 0 or previous value
            setEditingEvent(null);
            return;
          }
        } else {
          await api.delete(`${url}/${existingEventResponse.data[0].id}`);
          const newEvents = { ...events };
          delete newEvents[`${date}-${hour}`];
          const newTotal =
            totalModelsFinished - existingEventResponse.data[0].models_finished;
          setTotalModelsFinished(newTotal);

          if (newTotal > planningData.qte) {
            toast.error(
              "La quantité ne peut pas être supérieure à Quantité Société de modèle."
            );
            setEvents({ ...events, [`${date}-${hour}`]: 0 }); // Reset to 0 or previous value
            setEditingEvent(null);
            return;
          }

          setEvents(newEvents);
        }
      } else if (eventValue) {
        await api.post(url, {
          product_plan_id: planningData.id,
          date: date,
          hour: hour,
          models_finished: eventValue,
        });
        const newTotal = totalModelsFinished + eventValue;
        setTotalModelsFinished(newTotal);

        if (newTotal > planningData.qte) {
          toast.error(
            "La quantité ne peut pas être supérieure à Quantité Société de modèle."
          );
          setEvents({ ...events, [`${date}-${hour}`]: 0 }); // Reset to 0 or previous value
          setEditingEvent(null);
          return;
        }
      }

      // Update total models finished
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          "Impossible d'enregistrer cette quantité. Veuillez réessayer."
      );
      await fetchEvents();
    }

    setEditingEvent(null);
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault(); // Prevent the default scrolling behavior of arrow keys
      await handleEventBlur();

      const { date, hour } = editingEvent;
      const dateIndex = datesRange.findIndex((d) => format(d, "yyyy-MM-dd") === date);
      const currentDate = datesRange[dateIndex];
      const workSlots = getWorkSlotsForDate(currentDate);
      const hourIndex = workSlots.findIndex((slot) => slot.hour === hour);
  
      // Determine the next input to focus on based on the key pressed
      if (e.key === 'ArrowRight') {
        // Move to the next hour
        if (hourIndex < workSlots.length - 1) {
          const nextHour = workSlots[hourIndex + 1].hour;
          setEditingEvent({ date, hour: nextHour });
        } else if (dateIndex < datesRange.length - 1) {
          // Move to the next date, first hour
          const nextDate = format(addDays(datesRange[dateIndex], 1), "yyyy-MM-dd");
          const nextWorkSlots = getWorkSlotsForDate(datesRange[dateIndex + 1]);
          setEditingEvent({ date: nextDate, hour: nextWorkSlots[0].hour });
        }
      } else if (e.key === 'ArrowLeft') {
        // Move to the previous hour
        if (hourIndex > 0) {
          setEditingEvent({ date, hour: workSlots[hourIndex - 1].hour });
        } else if (dateIndex > 0) {
          // Move to the previous date, last hour
          const prevDate = format(addDays(datesRange[dateIndex], -1), "yyyy-MM-dd");
          const previousWorkSlots = getWorkSlotsForDate(datesRange[dateIndex - 1]);
          setEditingEvent({
            date: prevDate,
            hour: previousWorkSlots[previousWorkSlots.length - 1].hour,
          });
        }
      }
  
      // Automatically trigger a focus event on the new input after changing
      setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    }
  };
  

  return (
    <div className="pt-5 pr-1">
      <ToastContainer />
      <div className="mb-4 flex items-center justify-between">
        <div className="font-semibold text-xl mb-[0.5rem]">
          Date de lancement: {planningData.start_date}
        </div>
        <div className="font-semibold text-xl mb-[0.5rem]">
          Quantity Société: {planningData.qte}
                <button
                    className='ml-7 bg-blue-500 p-[10px] font-bold rounded text-white'
                    onClick={() => {
                        const link = document.createElement('a');
                        link.href = planningData.file_path;
                        link.download = planningData.file_path.split('/').pop();
                        link.click();
                    }}
                >
                    Télécharger Excel
                </button>
        </div>
      </div>
      <div className="mb-4 flex items-center justify-between">
        <div className="font-semibold text-xl mb-[3rem]">
          Cumul: {totalModelsFinished}
        </div>
        <div className="font-semibold text-xl mb-[3rem]">
          Quantity restante: {planningData.qte - totalModelsFinished}
        </div>
      </div>
      <div
        className="overflow-auto relative"
        style={{ maxHeight: "550px", maxWidth: "1400px" }}
      >
        <div className="min-w-max space-y-1">
          {datesRange.map((date) => {
            const formattedDate = format(date, "yyyy-MM-dd");
            const dayOfWeek = format(date, "EEEE", { locale: fr }); // Format day of week in French
            const isWeekend = date.getDay() === 0 || date.getDay() === 6; // Check if Saturday or Sunday
            const currentSchedule = getScheduleForDate(date);
            const scheduledHours = new Set(currentSchedule.map((slot) => slot.hour));
            const legacySlots = Object.keys(events)
              .filter((key) => key.startsWith(`${formattedDate}-`))
              .map((key) => key.slice(formattedDate.length + 1))
              .filter((hour) => !scheduledHours.has(hour))
              .sort()
              .map((hour) => ({ hour, label: `${hour} (ancien horaire)`, legacy: true }));
            const schedule = [...currentSchedule, ...legacySlots];
            const plannedHours = getPlannedHoursForDate(date);
            return (
              <div className="flex gap-1" key={formattedDate}>
                <div
                  className={`w-[150px] shrink-0 font-bold border items-center justify-center flex p-2 sticky left-0 z-20 ${
                    isWeekend ? "bg-red-100" : "bg-white"
                  }`}
                  style={{ height: "120px" }}
                >
                  <div className="text-center">
                    <div>{dayOfWeek} {date.getDate()}</div>
                    <div className="text-xs mt-1">
                      {String(plannedHours).replace(".", ",")} h
                    </div>
                  </div>
                </div>
                {schedule.map((slot) => (
                  <div
                    key={`${formattedDate}-${slot.hour}`}
                    className={`border relative h-[120px] w-[120px] shrink-0 z-0 border-black border-r-2 border-b-2 ${
                      slot.pause
                        ? "bg-amber-100 cursor-default"
                        : isWeekend
                          ? "bg-red-100 cursor-pointer"
                          : slot.legacy
                            ? "bg-purple-50 cursor-pointer"
                          : slot.overtime
                            ? "bg-blue-50 cursor-pointer"
                            : "bg-white cursor-pointer"
                    }`}
                    onClick={() => {
                      if (!slot.pause) {
                        handleDoubleClick(formattedDate, slot.hour);
                      }
                    }}
                  >
                    <div className="h-[38px] px-1 flex items-center justify-center text-center text-xs font-semibold border-b bg-slate-50">
                      {slot.label}
                    </div>
                    <div className="h-[80px] p-2 flex items-center justify-center font-semibold">
                    {
                    slot.pause ? (
                      <span className="text-amber-800 tracking-wide">PAUSE</span>
                    ) : editingEvent &&
                    editingEvent.date === formattedDate &&
                    editingEvent.hour === slot.hour ? (
                      <input
                          ref={inputRef} // Attach the ref here
                          className="w-full h-full border-black"
                          type="number"
                          value={events[`${formattedDate}-${slot.hour}`] || ""}
                          onChange={handleEventChange}
                          onKeyDown={handleKeyDown}
                          onBlur={handleEventBlur}
                        />
                      ) : (
                      events[`${formattedDate}-${slot.hour}`] || ""
                    )}
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default FullCalendar;
